import React, { useState } from 'react';
import { FaQuestionCircle, FaTimes } from 'react-icons/fa';

const AdvancedAIControls = ({ 
  advancedParams, 
  setAdvancedParams, 
  currentModelConfig 
}) => {
  const [helpModal, setHelpModal] = useState(null);

  // Help content for each parameter
  const helpContent = {
    temperature: {
      title: "Temperature",
      description: "Controls the randomness and creativity of AI responses. Lower values make outputs more deterministic and consistent.",
      examples: {
        low: "0.0-0.2: Highly consistent, same analysis every time. Perfect for forensic work requiring reproducible results.",
        high: "1.5-2.0: Creative and varied responses. May introduce inconsistencies but can catch nuanced details."
      },
      forensicRecommendation: "Use 0.1 for legal proceedings, 0.3-0.5 for exploratory analysis."
    },
    top_p: {
      title: "Top-P (Nucleus Sampling)",
      description: "Controls vocabulary diversity by limiting token selection to the top probability mass.",
      examples: {
        low: "0.1-0.3: Very focused vocabulary, predictable word choices. May miss nuanced descriptions.",
        high: "0.8-1.0: Diverse vocabulary, more descriptive language. May include less relevant terms."
      },
      forensicRecommendation: "Use 0.8 for balanced forensic terminology without being overly restrictive."
    },
    top_k: {
      title: "Top-K (Token Selection)",
      description: "Limits the AI to choosing from only the K most likely next words.",
      examples: {
        low: "1-10: Extremely focused, may sound robotic. Very predictable but limited expressiveness.",
        high: "80-100: More natural language but potentially less precise. May use unexpected terminology."
      },
      forensicRecommendation: "Use 40 for professional forensic language that remains precise."
    },
    frequency_penalty: {
      title: "Frequency Penalty",
      description: "Reduces repetition by penalizing tokens that appear frequently in the response.",
      examples: {
        low: "0.0: No penalty, may repeat phrases like 'the vehicle appears to be' multiple times.",
        high: "2.0: Strong anti-repetition, forces varied language but may become unnatural."
      },
      forensicRecommendation: "Use 0.2-0.5 to maintain professional consistency while avoiding redundancy."
    },
    presence_penalty: {
      title: "Presence Penalty", 
      description: "Encourages the AI to talk about new topics by penalizing tokens that have already appeared.",
      examples: {
        low: "0.0: May focus heavily on obvious features, potentially missing subtle details.",
        high: "2.0: Forces discussion of many different aspects, may include irrelevant observations."
      },
      forensicRecommendation: "Use 0.1-0.3 to ensure comprehensive coverage without losing focus."
    },
    chain_of_thought: {
      title: "Chain-of-Thought Reasoning",
      description: "Forces the AI to show its step-by-step reasoning process before conclusions.",
      examples: {
        disabled: "Direct conclusions: 'The vehicle is a blue sedan with license plate ABC123.'",
        enabled: "Reasoning shown: 'I can see a four-door vehicle. The color appears blue under the lighting. Looking at the rear, I can make out the characters A-B-C-1-2-3 on the license plate. Therefore, this is a blue sedan with plate ABC123.'"
      },
      forensicRecommendation: "Enable for complex analysis where reasoning transparency is crucial for legal proceedings."
    },
    few_shot_learning: {
      title: "Few-Shot Learning",
      description: "Provides example analyses to guide the AI's response format and quality.",
      examples: {
        disabled: "AI uses only its training knowledge, may vary in format and detail level.",
        enabled: "AI follows provided examples, ensuring consistent format and thoroughness matching your standards."
      },
      forensicRecommendation: "Enable with high-quality examples to standardize analysis format across cases."
    },
    retrieval_augmented: {
      title: "Retrieval-Augmented Generation",
      description: "Allows AI to reference external knowledge bases or previous case analyses.",
      examples: {
        disabled: "Analysis based only on the current evidence and AI's training data.",
        enabled: "Can reference vehicle databases, previous similar cases, or forensic best practices during analysis."
      },
      forensicRecommendation: "Enable when you have relevant databases or want consistency with previous cases."
    },
    multi_agent_reasoning: {
      title: "Multi-Agent Reasoning",
      description: "Uses multiple AI perspectives to cross-validate findings and reduce errors.",
      examples: {
        disabled: "Single AI analysis, faster but may miss details or make errors.",
        enabled: "Multiple AI agents analyze independently, then compare results. Slower but more accurate and reliable."
      },
      forensicRecommendation: "Enable for high-stakes cases where accuracy is more important than speed."
    },
    confidence_calibration: {
      title: "Confidence Calibration",
      description: "Adjusts how the AI calculates and reports confidence scores.",
      examples: {
        conservative: "Reports lower confidence scores, errs on side of caution. May underestimate certainty.",
        aggressive: "Reports higher confidence scores, more decisive. May overestimate certainty."
      },
      forensicRecommendation: "Use conservative for legal proceedings, balanced for standard investigations."
    },
    evidence_quality_threshold: {
      title: "Evidence Quality Threshold",
      description: "Minimum confidence level required to report a finding in the results.",
      examples: {
        low: "0.3: Reports uncertain findings, may include false positives but catches subtle details.",
        high: "0.8: Only reports high-confidence findings, may miss subtle but valid observations."
      },
      forensicRecommendation: "Use 0.7 for balanced forensic analysis, 0.8+ for court-ready evidence."
    },
    speculation_prevention: {
      title: "Speculation Prevention",
      description: "Controls how strictly the AI avoids making inferences beyond direct observations.",
      examples: {
        strict: "Only reports what is directly visible: 'A dark-colored vehicle is present.'",
        relaxed: "Allows reasonable inferences: 'The vehicle appears to be black, though lighting conditions may affect color perception.'"
      },
      forensicRecommendation: "Use strict for initial analysis, moderate for investigative insights."
    }
  };

  const HelpModal = ({ paramKey }) => {
    const content = helpContent[paramKey];
    if (!content) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: '#1a1a1a',
          border: '1px solid #404040',
          borderRadius: '12px',
          padding: '25px',
          maxWidth: '600px',
          maxHeight: '80vh',
          overflow: 'auto',
          color: '#ffffff'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, color: '#ff6b6b' }}>{content.title}</h3>
            <button
              onClick={() => setHelpModal(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#cccccc',
                cursor: 'pointer',
                fontSize: '18px'
              }}
            >
              <FaTimes />
            </button>
          </div>
          
          <div style={{ marginBottom: '20px', lineHeight: '1.6' }}>
            <p style={{ color: '#cccccc' }}>{content.description}</p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ color: '#4ade80', marginBottom: '10px' }}>Examples:</h4>
            <div style={{ background: '#2a2a2a', padding: '15px', borderRadius: '8px', marginBottom: '10px' }}>
              <strong style={{ color: '#60a5fa' }}>Low/Conservative:</strong>
              <p style={{ margin: '5px 0', color: '#cccccc' }}>{content.examples.low || content.examples.disabled}</p>
            </div>
            <div style={{ background: '#2a2a2a', padding: '15px', borderRadius: '8px' }}>
              <strong style={{ color: '#f59e0b' }}>High/Aggressive:</strong>
              <p style={{ margin: '5px 0', color: '#cccccc' }}>{content.examples.high || content.examples.enabled}</p>
            </div>
          </div>

          <div style={{ background: '#2a2a2a', padding: '15px', borderRadius: '8px', border: '1px solid #404040' }}>
            <h4 style={{ color: '#ec4899', marginBottom: '10px' }}>🎯 Forensic Recommendation:</h4>
            <p style={{ margin: 0, color: '#cccccc', fontStyle: 'italic' }}>{content.forensicRecommendation}</p>
          </div>
        </div>
      </div>
    );
  };

  const ParameterControl = ({ 
    label, 
    paramKey, 
    type = 'range', 
    min, 
    max, 
    step, 
    options, 
    description,
    enabled = true 
  }) => (
    <div style={{ 
      background: '#1a1a1a', 
      padding: '15px', 
      borderRadius: '8px', 
      border: '1px solid #404040',
      opacity: enabled ? 1 : 0.5
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ color: '#cccccc', fontSize: '12px', fontWeight: 'bold' }}>
            {label}
          </label>
          <button
            onClick={() => setHelpModal(paramKey)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#60a5fa',
              cursor: 'pointer',
              fontSize: '12px'
            }}
            title="Click for detailed explanation"
          >
            <FaQuestionCircle />
          </button>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#888' }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => {
              const newParams = { ...advancedParams };
              newParams[`${paramKey}_enabled`] = e.target.checked;
              setAdvancedParams(newParams);
            }}
            style={{ transform: 'scale(0.8)' }}
          />
          Enable
        </label>
      </div>

      {description && (
        <div style={{ fontSize: '10px', color: '#888', marginBottom: '10px' }}>
          {description}
        </div>
      )}

      {enabled && (
        <>
          {type === 'range' && (
            <>
              <div style={{ fontSize: '11px', color: '#cccccc', marginBottom: '5px' }}>
                Current: {advancedParams[paramKey]}
              </div>
              <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={advancedParams[paramKey]}
                onChange={(e) => setAdvancedParams({
                  ...advancedParams, 
                  [paramKey]: parseFloat(e.target.value)
                })}
                style={{ width: '100%' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#666', marginTop: '2px' }}>
                <span>{min}</span>
                <span>{max}</span>
              </div>
            </>
          )}

          {type === 'select' && (
            <select
              value={advancedParams[paramKey]}
              onChange={(e) => setAdvancedParams({
                ...advancedParams, 
                [paramKey]: e.target.value
              })}
              style={{
                width: '100%',
                padding: '5px',
                background: '#111111',
                color: '#ffffff',
                border: '1px solid #404040',
                borderRadius: '4px',
                fontSize: '11px'
              }}
            >
              {options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}

          {type === 'checkbox' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={advancedParams[paramKey]}
                onChange={(e) => setAdvancedParams({
                  ...advancedParams, 
                  [paramKey]: e.target.checked
                })}
                style={{ transform: 'scale(1.2)' }}
              />
              <span style={{ color: '#cccccc', fontSize: '11px' }}>
                {advancedParams[paramKey] ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div style={{ 
      background: '#2a2a2a', 
      padding: '15px 25px', 
      borderRadius: '12px', 
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      border: '1px solid #404040',
      marginBottom: '20px'
    }}>
      <h3 style={{ margin: '0 0 15px 0', color: '#ffffff' }}>
        ⚙️ Advanced AI Parameters & Prompt Engineering Controls
      </h3>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>
        
        {/* Core Model Parameters */}
        <ParameterControl
          label="Temperature"
          paramKey="temperature"
          type="range"
          min={0}
          max={2}
          step={0.1}
          description="Creativity vs consistency control"
          enabled={advancedParams.temperature_enabled !== false}
        />

        <ParameterControl
          label="Top-P (Nucleus Sampling)"
          paramKey="top_p"
          type="range"
          min={0.1}
          max={1}
          step={0.1}
          description="Vocabulary diversity control"
          enabled={advancedParams.top_p_enabled !== false}
        />

        <ParameterControl
          label="Top-K (Token Selection)"
          paramKey="top_k"
          type="range"
          min={1}
          max={100}
          step={1}
          description="Word choice limitation"
          enabled={advancedParams.top_k_enabled !== false}
        />

        <ParameterControl
          label="Frequency Penalty"
          paramKey="frequency_penalty"
          type="range"
          min={0}
          max={2}
          step={0.1}
          description="Reduces repetitive language"
          enabled={advancedParams.frequency_penalty_enabled !== false}
        />

        <ParameterControl
          label="Presence Penalty"
          paramKey="presence_penalty"
          type="range"
          min={0}
          max={2}
          step={0.1}
          description="Encourages topic diversity"
          enabled={advancedParams.presence_penalty_enabled !== false}
        />

        <ParameterControl
          label="Max Output Tokens"
          paramKey="max_output_tokens"
          type="range"
          min={512}
          max={16384}
          step={512}
          description="Response length limit (affects cost)"
          enabled={advancedParams.max_output_tokens_enabled !== false}
        />

        {/* Forensic-Specific Controls */}
        <ParameterControl
          label="Evidence Quality Threshold"
          paramKey="evidence_quality_threshold"
          type="range"
          min={0.1}
          max={1}
          step={0.1}
          description="Minimum confidence to report findings"
          enabled={advancedParams.evidence_quality_threshold_enabled !== false}
        />

        <ParameterControl
          label="Speculation Prevention"
          paramKey="speculation_prevention"
          type="select"
          options={[
            { value: 'strict', label: 'Strict - No speculation allowed' },
            { value: 'moderate', label: 'Moderate - Limited inference' },
            { value: 'relaxed', label: 'Relaxed - Educated guesses OK' }
          ]}
          description="How strictly to avoid speculation"
          enabled={advancedParams.speculation_prevention_enabled !== false}
        />

        <ParameterControl
          label="Analysis Verbosity"
          paramKey="verbosity"
          type="select"
          options={[
            { value: 'brief', label: 'Brief - Key findings only' },
            { value: 'standard', label: 'Standard - Balanced detail' },
            { value: 'detailed', label: 'Detailed - Comprehensive analysis' },
            { value: 'comprehensive', label: 'Comprehensive - Maximum detail' }
          ]}
          description="Level of analysis detail"
          enabled={advancedParams.verbosity_enabled !== false}
        />

        <ParameterControl
          label="Confidence Display Mode"
          paramKey="confidence_display"
          type="select"
          options={[
            { value: 'explicit', label: 'Explicit - Show confidence reasoning' },
            { value: 'numeric', label: 'Numeric - Numbers only' },
            { value: 'qualitative', label: 'Qualitative - High/Medium/Low' }
          ]}
          description="How to display confidence scores"
          enabled={advancedParams.confidence_display_enabled !== false}
        />

        {/* Advanced Prompting Techniques */}
        <ParameterControl
          label="Chain-of-Thought Reasoning"
          paramKey="chain_of_thought"
          type="checkbox"
          description="Forces step-by-step reasoning display"
          enabled={advancedParams.chain_of_thought_enabled !== false}
        />

        <ParameterControl
          label="Few-Shot Learning"
          paramKey="few_shot_learning"
          type="checkbox"
          description="Uses examples to guide response format"
          enabled={advancedParams.few_shot_learning_enabled !== false}
        />

        <ParameterControl
          label="Retrieval-Augmented Generation"
          paramKey="retrieval_augmented"
          type="checkbox"
          description="References external knowledge bases"
          enabled={advancedParams.retrieval_augmented_enabled !== false}
        />

        <ParameterControl
          label="Multi-Agent Reasoning"
          paramKey="multi_agent_reasoning"
          type="checkbox"
          description="Uses multiple AI perspectives for validation"
          enabled={advancedParams.multi_agent_reasoning_enabled !== false}
        />

        <ParameterControl
          label="Confidence Calibration"
          paramKey="confidence_calibration"
          type="select"
          options={[
            { value: 'conservative', label: 'Conservative - Lower confidence scores' },
            { value: 'balanced', label: 'Balanced - Standard calibration' },
            { value: 'aggressive', label: 'Aggressive - Higher confidence scores' }
          ]}
          description="How to calibrate confidence reporting"
          enabled={advancedParams.confidence_calibration_enabled !== false}
        />
      </div>

      {helpModal && <HelpModal paramKey={helpModal} />}
    </div>
  );
};

export default AdvancedAIControls;
